// @ts-check
/* ═══════════════════════ OFFLINE-DOKUMENTVAULT (#3) ═══════════════════════
   Pass, forsikringsbevis, billetter og eTA MÅ være tilgjengelig uten nett
   — typisk ved grensepassering Canada↔USA eller på cruiset. Den eksisterende
   docs.js krever Supabase-innlogging og signerte URL-er (5 min, online).

   Her lagrer vi dokument-bildene LOKALT på enheten i IndexedDB, bak en PIN.
   Ingen server, ingen nett — åpner umiddelbart via blob-URL. Dette er en
   bevisst, separat «offline-koffert» ved siden av sky-arkivet.

   Sikkerhet: PIN-en hindrer skuldersurfing hvis noen plukker opp telefonen.
   Den krypterer ikke disken (det gjør enhetens egen kryptering) — så ikke en
   erstatning for telefonens skjermlås, men et ekstra lag for sensitive bilder.
   ═══════════════════════════════════════════════════════════════════════════ */

const DB_NAME = 'ca26vault';
const STORE = 'docs';
const PIN_KEY = 'ca26vault_pin';      // SHA-256-hash av PIN (localStorage)
const UNLOCK_KEY = 'ca26vault_unlocked'; // sesjonsflagg (sessionStorage)

let _db = null;

function openDB(){
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => { _db = req.result; resolve(_db); };
    req.onerror = () => reject(req.error);
  });
}

function tx(mode){
  return openDB().then(db => db.transaction(STORE, mode).objectStore(STORE));
}

export async function vaultList(){
  const store = await tx('readonly');
  return new Promise((resolve) => {
    const out = [];
    const cur = store.openCursor();
    cur.onsuccess = () => {
      const c = cur.result;
      if (c) { const { blob, ...meta } = c.value; out.push(meta); c.continue(); }
      else resolve(out.sort((a, b) => b.createdAt - a.createdAt));
    };
    cur.onerror = () => resolve(out);
  });
}

export async function vaultAdd({ label, category, mime, blob }){
  const store = await tx('readwrite');
  const id = crypto.randomUUID();
  const rec = { id, label, category, mime, blob, size: blob.size, createdAt: Date.now() };
  return new Promise((resolve, reject) => {
    const r = store.add(rec);
    r.onsuccess = () => resolve(id);
    r.onerror = () => reject(r.error);
  });
}

export async function vaultGetBlob(id){
  const store = await tx('readonly');
  return new Promise((resolve) => {
    const r = store.get(id);
    r.onsuccess = () => resolve(r.result?.blob || null);
    r.onerror = () => resolve(null);
  });
}

export async function vaultDelete(id){
  const store = await tx('readwrite');
  return new Promise((resolve) => {
    const r = store.delete(id);
    r.onsuccess = () => resolve(true);
    r.onerror = () => resolve(false);
  });
}

export async function vaultUsage(){
  const list = await vaultList();
  return { count: list.length, bytes: list.reduce((s, d) => s + (d.size || 0), 0) };
}

/* ── PIN ── */
async function sha256(str){
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, '0')).join('');
}

export function hasPin(){ return !!localStorage.getItem(PIN_KEY); }

export async function setPin(pin){
  if (!pin || pin.length < 4) return false;
  localStorage.setItem(PIN_KEY, await sha256(pin));
  sessionStorage.setItem(UNLOCK_KEY, '1');
  return true;
}

export async function verifyPin(pin){
  const stored = localStorage.getItem(PIN_KEY);
  if (!stored) return false;
  const ok = (await sha256(pin)) === stored;
  if (ok) sessionStorage.setItem(UNLOCK_KEY, '1');
  return ok;
}

export function isUnlocked(){
  // Uten PIN er vaulten åpen; med PIN kreves opplåsing denne sesjonen.
  return !hasPin() || sessionStorage.getItem(UNLOCK_KEY) === '1';
}

export function lock(){ sessionStorage.removeItem(UNLOCK_KEY); }

export function removePin(){
  localStorage.removeItem(PIN_KEY);
  sessionStorage.removeItem(UNLOCK_KEY);
}
